import { useEffect, useState } from 'react';
import cfApi from '../cognitive-flows/utils/api';

// The dashboard theme — "blue" (classic, the default) or "green" — chosen per
// person in Settings → General → Appearance.
//
//   - Applied as <html data-theme="…">; themeTokens.css defines every green
//     token under [data-theme="green"] only, so "blue" is the untouched app.
//   - Kept in localStorage for an instant first paint (initTheme runs before
//     React renders), and on the server (GET/PATCH
//     /api/cognitive-flows/preferences/ui, per user and account) so it follows
//     the person to another browser.
//   - useTheme() re-renders a component when the theme changes, for the few
//     colours that can't be CSS variables (SVG presentation attributes and
//     chart props) — read them with themeColor().

export const THEMES = Object.freeze(['blue', 'green']);
export const DEFAULT_THEME = 'blue';
const STORAGE_KEY = 'cux_theme';
const EVENT = 'cux-theme-change';

let current = DEFAULT_THEME;

function normalize(theme) {
  return THEMES.includes(theme) ? theme : DEFAULT_THEME;
}

function readStored() {
  try { return normalize(localStorage.getItem(STORAGE_KEY)); } catch { return DEFAULT_THEME; }
}

function writeStored(theme) {
  try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* private mode */ }
}

/** Put a theme on the page (no persistence). */
export function applyTheme(theme) {
  current = normalize(theme);
  if (typeof document !== 'undefined') {
    if (current === DEFAULT_THEME) delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = current;
    document.dispatchEvent(new CustomEvent(EVENT, { detail: current }));
  }
  return current;
}

export function getTheme() {
  return current;
}

/** Before the first render: the theme this browser last used. */
export function initTheme() {
  return applyTheme(readStored());
}

/**
 * Change the theme: applied at once, remembered in this browser, and saved to
 * the person's preferences (best-effort — a failed save keeps the local choice).
 */
export async function setTheme(theme, { persist = true } = {}) {
  const next = applyTheme(theme);
  writeStored(next);
  if (persist) {
    try {
      await cfApi.patch('/preferences/ui', { theme: next });
    } catch {
      // Offline or signed out: the browser still remembers it.
    }
  }
  return next;
}

/** After sign-in: adopt the theme saved on the server, if it differs. */
export async function syncThemeFromServer() {
  try {
    const { data } = await cfApi.get('/preferences/ui');
    const saved = normalize(data?.theme);
    if (saved !== current) {
      applyTheme(saved);
      writeStored(saved);
    }
    return saved;
  } catch {
    return current;
  }
}

/** [theme, setTheme] — re-renders on every theme change. */
export function useTheme() {
  const [theme, setLocal] = useState(current);
  useEffect(() => {
    const onChange = (e) => setLocal(e.detail);
    document.addEventListener(EVENT, onChange);
    setLocal(current);
    return () => document.removeEventListener(EVENT, onChange);
  }, []);
  return [theme, setTheme];
}

/**
 * The value of a theme token for places CSS variables can't reach (SVG
 * presentation attributes, chart props). Falls back when the token is unset —
 * i.e. always in the classic blue theme.
 */
export function themeColor(token, fallback) {
  if (typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  return v || fallback;
}
